import type { QueryClient } from '@tanstack/react-query';
import { reviewKeys } from '@/lib/queryKeys';

/**
 * The review list and everything derived from it.
 *
 * This is the right call for row edits — an internal note, an escalation flag,
 * a testimonial approval, an appointment link, a regenerated draft. None of
 * them can move a figure in the top strip: `total`, `averageRating`,
 * `responseRate`, `medianTimeToReplyHours` and `trend30d` are computed from
 * counts, ratings and PUBLISHED reply state, and a note changes none of those.
 *
 * Two functions rather than one with a flag, because the wrong choice should be
 * visible at the call site. A boolean argument defaults silently; a name has to
 * be read. Raised in review on #2434.
 */
export function invalidateReviewViews(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: reviewKeys.lists() });
}

/**
 * The list, plus the aggregate strip.
 *
 * Reserved for mutations that change what the strip measures — in practice
 * publishing or recording a reply, which moves `responseRate` and
 * `medianTimeToReplyHours`.
 *
 * The strip used to be derived from the same list query every mutation already
 * invalidated, so refreshing the list refreshed the numbers for free. Giving it
 * its own query removed that, and a stale `responseRate` looks exactly like a
 * correct one — which is why this exists at all.
 *
 * Deliberately narrower than `reviewKeys.all()`: that would also drop cached
 * reply templates, which no review mutation changes.
 */
export function invalidateReviewViewsAndStats(queryClient: QueryClient): void {
  invalidateReviewViews(queryClient);
  void queryClient.invalidateQueries({ queryKey: reviewKeys.statsAll() });
}
